import { Injectable, computed, signal } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * Service for simulation wizard header save integration.
 * Mirrors the shared wizard header-save API so the header can treat different wizards uniformly.
 */
@Injectable({
  providedIn: 'root',
})
export class SimulationWizardService {
  /** True when the simulation wizard is the active route. */
  private readonly _wizardActive = signal(false);
  /** True when the wizard is in readonly mode (no edits). */
  private readonly _wizardReadOnly = signal(true);
  /** True when the current wizard step has unsaved local changes. */
  private readonly _hasUnsavedChanges = signal(false);

  /** Show the header save button when the wizard is active and not readonly. */
  readonly showSaveInHeader = computed(
    () => this._wizardActive() && !this._wizardReadOnly()
  );

  /** Show a red unsaved indicator dot on the save icon. */
  readonly showUnsavedIndicator = computed(
    () => this.showSaveInHeader() && this._hasUnsavedChanges()
  );

  private readonly saveRequested = new Subject<void>();
  /** Emits when the header save button is clicked. Wizard should subscribe and save. */
  readonly onSaveRequested = this.saveRequested.asObservable();

  private readonly consumptionValidationRequested = new Subject<void>();
  /** Emits when the wizard wants Step 2 inline validation errors to be revealed. */
  readonly onConsumptionValidationRequested = this.consumptionValidationRequested.asObservable();

  /** Call when the simulation wizard component becomes active or when its readonly state changes. */
  setWizardContext(active: boolean, readOnly?: boolean): void {
    this._wizardActive.set(active);
    if (active && readOnly !== undefined) {
      this._wizardReadOnly.set(readOnly);
    }
    if (!active) {
      this._wizardReadOnly.set(true);
      this._hasUnsavedChanges.set(false);
    }
  }

  /** Mark that the current step has unsaved changes. */
  markUnsavedChanges(): void {
    if (!this._wizardActive() || this._wizardReadOnly()) return;
    this._hasUnsavedChanges.set(true);
  }

  /** Mark that all changes are saved. */
  markSaved(): void {
    this._hasUnsavedChanges.set(false);
  }

  /** Called by the header when the user clicks Save. */
  requestSave(): void {
    this.saveRequested.next();
  }

  /** Called by the wizard when navigation is blocked by Step 2 validation. */
  requestConsumptionValidation(): void {
    this.consumptionValidationRequested.next();
  }
}
